import type { ChangeEventHandler, FC } from "react";

import type { UseFormReturn } from "react-hook-form";

import { Input, Text } from "@alpacahq/ui/old";

import { NumericInput } from "~/v2/components/form/NumericInput";
import type { MARITAL_STATUS_VALUES } from "../../constant";
import { Dropdown } from "./FinancialInputs";
import type { FinancialProfileForm } from "./schema";

export const DependentsInput = ({
  id,
  label,
  value,
  onChange,
  errorMessage,
  placeholder,
}: {
  id: string;
  label: string;
  value: string | number;
  onChange: (value: number) => void;
  errorMessage?: string;
  placeholder?: string;
}) => {
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.currentTarget.value;
    onChange(value === "" ? NaN : Number(value));
  };

  return (
    <div className="mb-8 w-full max-w-60">
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="block pb-1 font-bold text-base leading-5"
        >
          {label}
        </label>
        {errorMessage && (
          <Text className="text-red-500 text-sm">{errorMessage}</Text>
        )}
      </div>
      <NumericInput
        id={id}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full bg-transparent"
        customInput={Input}
        thousandSeparator={false}
      />
    </div>
  );
};

export const MARITAL_STATUS_OPTIONS: {
  label: string;
  value: (typeof MARITAL_STATUS_VALUES)[number];
}[] = [
  { label: "Single", value: "SINGLE" },
  { label: "Married", value: "MARRIED" },
  { label: "Divorced", value: "DIVORCED" },
  { label: "Widowed", value: "WIDOWED" },
];

type MaritalStatusProps = {
  form: UseFormReturn<FinancialProfileForm>;
};

export const MaritalStatusInputs: FC<MaritalStatusProps> = ({ form }) => {
  const {
    watch,
    formState: { errors },
    setValue,
  } = form;

  const maritalStatus = watch("marital_status");
  const numDependents = watch("number_of_dependents");

  const onMaritalStatusChange = (value: string | string[]) => {
    const status: string = Array.isArray(value) ? value[0] : value;

    setValue(
      "marital_status",
      status.toUpperCase() as (typeof MARITAL_STATUS_VALUES)[number],
    );
  };

  const onNumberDependentsChange = (value: number) =>
    setValue("number_of_dependents", value);

  return (
    <div className="space-y-6">
      <Dropdown
        id="marital_status"
        label="Marital Status"
        value={maritalStatus}
        onChange={onMaritalStatusChange}
        options={MARITAL_STATUS_OPTIONS}
        errorMessage={errors.marital_status?.message}
        placeholder="Select"
      />
      <DependentsInput
        id="number_of_dependents"
        label="Number of Dependents"
        value={numDependents}
        onChange={onNumberDependentsChange}
        errorMessage={errors.number_of_dependents?.message}
        placeholder="Number of dependents"
      />
    </div>
  );
};
